import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  Globe, LogOut, Users, LayoutGrid, FileText, Plus, 
  Settings, CreditCard, Search, MoreVertical, Eye, Pencil, Trash2,
  ExternalLink, CheckCircle, Clock, XCircle, RefreshCw, Mail, MailOpen, ChevronDown, Send, ShoppingBag,
  Sparkles, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import SiteContentPage from '@/pages/SiteContentPage';
import SiteBuilder from '@/components/SiteBuilder';
import SiteEditor from '@/components/SiteEditor';
import MigrationWizard from '@/components/MigrationWizard';
import NewslettersView from '@/pages/NewslettersView';
import OrdersView from '@/pages/OrdersView';

// Navigation items
const navItems = [
  { id: 'sites', label: 'Sites', icon: LayoutGrid, path: '/hub' },
  { id: 'orders', label: 'Orders', icon: ShoppingBag, path: '/hub/orders' },
  { id: 'enquiries', label: 'Enquiries', icon: Mail, path: '/hub/enquiries' },
  { id: 'newsletters', label: 'Newsletters', icon: Send, path: '/hub/newsletters' },
  { id: 'backstage', label: 'Backstage', icon: Sparkles, path: '/hub/backstage' },
  { id: 'users', label: 'Users', icon: Users, path: '/hub/users' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/hub/billing' },
  { id: 'content', label: 'Marketing', icon: FileText, path: '/hub/content' },
];

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    published: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Live' },
    draft: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Draft' },
    archived: { icon: XCircle, color: 'bg-slate-100 text-slate-600', label: 'Archived' },
  };
  const { icon: Icon, color, label } = config[status] || config.draft;
  return (
    <Badge className={cn("flex items-center gap-1", color)}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Site Card Component
const SiteCard = ({ site, onEdit, onView, onDelete, onManage, onChangePackage }) => (
  <Card className="group hover:shadow-lg transition-all" data-testid={`site-card-${site.slug}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onManage(site)} data-testid={`site-card-title-${site.slug}`}>
          <CardTitle className="text-lg truncate">{site.performer_name}</CardTitle>
          <CardDescription className="truncate">{site.tagline || site.service_type || 'No tagline'}</CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`site-menu-${site.slug}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onManage(site)} data-testid={`site-edit-${site.slug}`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Site
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.open(`/sites/${site.slug}?preview=true`, '_blank')} data-testid={`site-preview-${site.slug}`}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </DropdownMenuItem>
            {onChangePackage && (
              <DropdownMenuItem onClick={() => onChangePackage(site)} data-testid={`site-change-package-${site.slug}`}>
                <Settings className="h-4 w-4 mr-2" />
                Change Package
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDelete(site)} className="text-red-600" data-testid={`site-delete-${site.slug}`}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusBadge status={site.status} />
          <Badge variant="outline" className="capitalize">{site.package}</Badge>
        </div>
        <span className="text-xs text-slate-400">{site.slug}.boxit.site</span>
      </div>
      {site.status === 'published' && (
        <a 
          href={`/sites/${site.slug}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="mt-3 flex items-center gap-1 text-sm text-orange-500 hover:text-orange-600"
          data-testid={`site-view-live-${site.slug}`}
        >
          <ExternalLink className="w-3 h-3" />
          View live site
        </a>
      )}
    </CardContent>
  </Card>
);

// Sites List View
function SitesView({ onCreateSite, onEditSite, onImportSite }) {
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [packageFilter, setPackageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ open: false, site: null });
  const [packageModal, setPackageModal] = useState({ open: false, site: null });
  const [newPackage, setNewPackage] = useState('');
  const navigate = useNavigate();

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data.sites || []);
    } catch (error) {
      toast.error('Failed to load sites');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleDelete = async () => {
    try {
      await api.delete(`/sites/${deleteModal.site.id}`);
      toast.success('Site deleted');
      setDeleteModal({ open: false, site: null });
      fetchSites();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const handleChangePackage = async () => {
    if (!newPackage) return;
    try {
      await api.put(`/sites/${packageModal.site.id}/package`, { package: newPackage });
      toast.success(`Package updated to ${newPackage}`);
      setPackageModal({ open: false, site: null });
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update package');
    }
  };

  const filteredSites = sites.filter(site => {
    if (packageFilter !== 'all' && site.package !== packageFilter) return false;
    if (statusFilter !== 'all' && site.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return site.performer_name.toLowerCase().includes(q) || site.slug.toLowerCase().includes(q);
    }
    return true;
  });

  // Package counts
  const pkgCounts = sites.reduce((acc, s) => { acc[s.package] = (acc[s.package] || 0) + 1; return acc; }, {});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Performer Sites</h2>
          <p className="text-slate-500">{sites.length} sites</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onImportSite} data-testid="import-site-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Import Site
          </Button>
          <Button onClick={onCreateSite} data-testid="create-site-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Site
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search sites..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-sites-input"
          />
        </div>

        <Select value={packageFilter} onValueChange={setPackageFilter}>
          <SelectTrigger className="w-[160px]" data-testid="package-filter">
            <SelectValue placeholder="All packages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All packages</SelectItem>
            <SelectItem value="starter">Starter ({pkgCounts.starter || 0})</SelectItem>
            <SelectItem value="professional">Professional ({pkgCounts.professional || 0})</SelectItem>
            <SelectItem value="business">Business ({pkgCounts.business || 0})</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredSites.length === 0 ? (
        <Card className="p-12 text-center">
          <LayoutGrid className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {searchQuery || packageFilter !== 'all' || statusFilter !== 'all' ? 'No sites found' : 'No sites yet'}
          </h3>
          <p className="text-slate-500 mb-4">
            {searchQuery || packageFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first performer site'}
          </p>
          {!searchQuery && packageFilter === 'all' && statusFilter === 'all' && (
            <Button onClick={onCreateSite}>
              <Plus className="w-4 h-4 mr-2" />
              Create Site
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSites.map(site => (
            <SiteCard 
              key={site.id}
              site={site}
              onManage={(s) => onEditSite(s)}
              onView={(s) => window.open(`/sites/${s.slug}`, '_blank')}
              onDelete={(s) => setDeleteModal({ open: true, site: s })}
              onChangePackage={(s) => { setPackageModal({ open: true, site: s }); setNewPackage(s.package); }}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, site: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteModal.site?.performer_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, site: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-site">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Change Modal */}
      <Dialog open={packageModal.open} onOpenChange={(open) => !open && setPackageModal({ open: false, site: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Package</DialogTitle>
            <DialogDescription>
              Update the package for "{packageModal.site?.performer_name}". This will adjust which modules are available.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label>Current package: <span className="font-bold capitalize">{packageModal.site?.package}</span></Label>
            <div>
              <Label>New package</Label>
              <Select value={newPackage} onValueChange={setNewPackage}>
                <SelectTrigger data-testid="package-change-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackageModal({ open: false, site: null })}>Cancel</Button>
            <Button onClick={handleChangePackage} data-testid="confirm-package-change">Update Package</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Users Management View
function UsersView() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userModal, setUserModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [form, setForm] = useState({ name: '', email: '', password: '', site_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, sitesRes] = await Promise.all([
        api.get('/hub/users'),
        api.get('/sites')
      ]);
      setUsers(usersRes.data.users || []);
      setSites(sitesRes.data.sites || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setForm({ name: user.name, email: user.email, password: '', site_id: user.site_id || '' });
    } else {
      setForm({ name: '', email: '', password: '', site_id: '' });
    }
    setUserModal({ open: true, user });
  };

  const handleSave = async () => {
    if (!form.name || !form.email || (!userModal.user && !form.password)) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (userModal.user) {
        const updateData = { name: form.name, email: form.email, site_id: form.site_id || null };
        if (form.password) updateData.password = form.password;
        await api.put(`/hub/users/${userModal.user.id}`, updateData);
        toast.success('User updated');
      } else {
        await api.post('/hub/users', { 
          ...form, 
          role: 'client',
          site_id: form.site_id || null 
        });
        toast.success('User created');
      }
      setUserModal({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/hub/users/${deleteModal.user.id}`);
      toast.success('User deleted');
      setDeleteModal({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.performer_name || 'Unassigned';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Client Users</h2>
          <p className="text-slate-500">{users.length} users</p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {users.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No users yet</h3>
          <p className="text-slate-500 mb-4">Create a user account for a client</p>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </Card>
      ) : (
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Assigned Site</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{user.name}</td>
                  <td className="px-4 py-3 text-slate-600">{user.email}</td>
                  <td className="px-4 py-3 text-slate-600">{getSiteName(user.site_id)}</td>
                  <td className="px-4 py-3">
                    <Badge className={user.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                      {user.active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenModal(user)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, user })} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Form Modal */}
      <Dialog open={userModal.open} onOpenChange={(open) => !open && setUserModal({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{userModal.user ? 'Edit User' : 'Create User'}</DialogTitle>
            <DialogDescription>
              {userModal.user ? 'Update user details' : 'Create a new client user account'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="John Smith"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                value={form.email} 
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="john@example.com"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{userModal.user ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
              <Input 
                type="password"
                value={form.password} 
                onChange={(e) => setForm({...form, password: e.target.value})}
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned Site</Label>
              <Select value={form.site_id || "unassigned"} onValueChange={(v) => setForm({...form, site_id: v === "unassigned" ? "" : v})}>
                <SelectTrigger data-testid="user-site-select">
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.performer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserModal({ open: false, user: null })}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-user-btn">
              {userModal.user ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteModal.user?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, user: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Enquiries Dashboard View
function EnquiriesView() {
  const [enquiries, setEnquiries] = useState([]);
  const [sitesLookup, setSitesLookup] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [siteFilter, setSiteFilter] = useState('all');

  const fetchEnquiries = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/enquiries');
      setEnquiries(res.data.enquiries || []);
      setSitesLookup(res.data.sites || {});
    } catch (err) {
      toast.error('Failed to load enquiries');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  const handleMarkRead = async (id, readStatus) => {
    try {
      await api.put(`/enquiries/${id}/read`, { read: readStatus });
      setEnquiries(prev => prev.map(e => e.id === id ? { ...e, read: readStatus } : e));
      if (selectedEnquiry?.id === id) setSelectedEnquiry(prev => ({ ...prev, read: readStatus }));
      toast.success(readStatus ? 'Marked as read' : 'Marked as unread');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries(prev => prev.filter(e => e.id !== id));
      if (selectedEnquiry?.id === id) setSelectedEnquiry(null);
      toast.success('Enquiry deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const unreadCount = enquiries.filter(e => !e.read).length;
  const siteNames = [...new Set(enquiries.map(e => e.site_id))].map(id => ({
    id,
    name: sitesLookup[id]?.performer_name || 'Unknown Site'
  }));

  const filtered = enquiries.filter(e => {
    if (filter === 'unread' && e.read) return false;
    if (filter === 'read' && !e.read) return false;
    if (siteFilter !== 'all' && e.site_id !== siteFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return e.name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q) || e.message?.toLowerCase().includes(q);
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="enquiries-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Enquiries</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {enquiries.length} total{unreadCount > 0 && <span className="text-orange-500 font-medium"> &middot; {unreadCount} unread</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchEnquiries} data-testid="refresh-enquiries">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search name, email, message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="enquiry-search"
          />
        </div>

        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {[{ key: 'all', label: 'All' }, { key: 'unread', label: 'Unread' }, { key: 'read', label: 'Read' }].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                filter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              )}
            >
              {f.label}
              {f.key === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-orange-500 text-white">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {siteNames.length > 1 && (
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="site-filter">
              <SelectValue placeholder="All sites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {siteNames.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">
              {enquiries.length === 0 ? 'No enquiries yet' : 'No matching enquiries'}
            </h3>
            <p className="text-sm text-slate-400">
              {enquiries.length === 0 ? 'Enquiries from contact forms will appear here' : 'Try adjusting your search or filters'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Enquiry List */}
          <div className="lg:col-span-2 space-y-1.5 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
            {filtered.map(enquiry => (
              <div
                key={enquiry.id}
                onClick={() => {
                  setSelectedEnquiry(enquiry);
                  if (!enquiry.read) handleMarkRead(enquiry.id, true);
                }}
                data-testid={`enquiry-item-${enquiry.id}`}
                className={cn(
                  "p-3.5 rounded-xl border cursor-pointer transition-all group",
                  selectedEnquiry?.id === enquiry.id
                    ? "bg-orange-50 border-orange-200 shadow-sm"
                    : enquiry.read
                      ? "bg-white border-slate-200 hover:border-slate-300"
                      : "bg-white border-l-4 border-l-orange-400 border-slate-200 hover:border-slate-300"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {!enquiry.read && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
                      <span className={cn("text-sm truncate", !enquiry.read ? "font-semibold text-slate-900" : "font-medium text-slate-700")}>
                        {enquiry.name}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{enquiry.email}</p>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{enquiry.message}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-slate-400 whitespace-nowrap">{formatDate(enquiry.created_at)}</p>
                    <p className="text-[10px] text-slate-400 mt-1 whitespace-nowrap">{sitesLookup[enquiry.site_id]?.performer_name || ''}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3">
            {selectedEnquiry ? (
              <Card data-testid="enquiry-detail-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{selectedEnquiry.name}</CardTitle>
                      <CardDescription className="mt-1">
                        <a href={`mailto:${selectedEnquiry.email}`} className="text-orange-600 hover:underline">{selectedEnquiry.email}</a>
                        {selectedEnquiry.phone && <span className="ml-3 text-slate-500">{selectedEnquiry.phone}</span>}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(selectedEnquiry.id, !selectedEnquiry.read)}
                        data-testid="toggle-read-btn"
                        className="text-slate-500 hover:text-slate-700"
                      >
                        {selectedEnquiry.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(selectedEnquiry.id)}
                        data-testid="delete-enquiry-btn"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(selectedEnquiry.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {sitesLookup[selectedEnquiry.site_id]?.performer_name || 'Unknown site'}
                    </span>
                    <Badge variant={selectedEnquiry.read ? "secondary" : "default"} className={cn("text-[10px]", !selectedEnquiry.read && "bg-orange-500")}>
                      {selectedEnquiry.read ? 'Read' : 'Unread'}
                    </Badge>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-5 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed border border-slate-100">
                    {selectedEnquiry.message}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => window.open(`mailto:${selectedEnquiry.email}?subject=Re: Enquiry from ${sitesLookup[selectedEnquiry.site_id]?.performer_name || 'Boxit'}&body=%0A%0A----%0AOriginal message:%0A${encodeURIComponent(selectedEnquiry.message)}`)}
                      data-testid="reply-email-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <Mail className="w-4 h-4 mr-1.5" /> Reply via Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-20 text-center">
                  <MailOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-400">Select an enquiry to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Billing View - placeholder that links to existing dashboard
// Backstage AI Credits View
function BackstageCreditsView() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [grantModal, setGrantModal] = useState({ open: false, site: null });
  const [grantAmount, setGrantAmount] = useState(50);
  const [grantReason, setGrantReason] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/ai/credits-overview');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load Backstage data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGrant = async () => {
    if (!grantModal.site || grantAmount <= 0) return;
    setGranting(true);
    try {
      await api.post('/ai/credits/grant', {
        site_id: grantModal.site.site_id,
        amount: grantAmount,
        reason: grantReason || 'admin_grant',
      });
      toast.success(`Granted ${grantAmount} credits to ${grantModal.site.performer_name}`);
      setGrantModal({ open: false, site: null });
      setGrantAmount(50);
      setGrantReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to grant credits');
    } finally {
      setGranting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const sites = data?.sites || [];
  const recentGrants = data?.recent_grants || [];

  return (
    <div className="space-y-6" data-testid="backstage-credits-view">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-500" />
          Backstage AI
        </h2>
        <p className="text-slate-500">Monitor AI credit usage across all sites</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Sites with Credits</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">{totals.total_sites_with_credits || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Credits Balance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" />
              <p className="text-3xl font-bold text-amber-600">{totals.total_balance || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Credits Used</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">{totals.total_used || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Sites Credits Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credits by Site</CardTitle>
          <CardDescription>Click "Grant" to add credits to a site</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Site</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Package</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Balance</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Used</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sites.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No sites have used Backstage yet. Credits are created when a user first opens the AI panel.
                    </td>
                  </tr>
                ) : (
                  sites.map((site) => (
                    <tr key={site.site_id} className="hover:bg-slate-50" data-testid={`credits-row-${site.slug}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{site.performer_name}</p>
                          <p className="text-xs text-slate-400">{site.slug}.boxit.site</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="capitalize">{site.package}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn(
                          "font-semibold",
                          site.balance > 10 ? "text-emerald-600" : site.balance > 0 ? "text-amber-600" : "text-red-600"
                        )}>
                          {site.balance}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{site.total_used}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGrantModal({ open: true, site })}
                          data-testid={`grant-credits-${site.slug}`}
                        >
                          <Zap className="w-3 h-3 mr-1" /> Grant
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Grants Log */}
      {recentGrants.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Credit Grants</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {recentGrants.map((grant) => {
                const siteName = sites.find(s => s.site_id === grant.site_id)?.performer_name || grant.site_id;
                return (
                  <div key={grant.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">+{grant.amount} credits to {siteName}</p>
                      <p className="text-xs text-slate-400">{grant.reason} &middot; {new Date(grant.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">+{grant.amount}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grant Credits Modal */}
      <Dialog open={grantModal.open} onOpenChange={(open) => !open && setGrantModal({ open: false, site: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Backstage Credits</DialogTitle>
            <DialogDescription>
              Add AI credits to {grantModal.site?.performer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Credits to Add</Label>
              <Input
                type="number"
                min={1}
                value={grantAmount}
                onChange={(e) => setGrantAmount(parseInt(e.target.value) || 0)}
                data-testid="grant-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="e.g. Welcome bonus, top-up request"
                data-testid="grant-reason-input"
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p>Current balance: <span className="font-semibold">{grantModal.site?.balance || 0}</span></p>
              <p>After grant: <span className="font-semibold text-emerald-600">{(grantModal.site?.balance || 0) + grantAmount}</span></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantModal({ open: false, site: null })}>Cancel</Button>
            <Button onClick={handleGrant} disabled={granting || grantAmount <= 0} data-testid="confirm-grant-btn">
              {granting ? 'Granting...' : `Grant ${grantAmount} Credits`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BillingView() {
  const [tab, setTab] = useState('subscriptions');
  const [subs, setSubs] = useState(null);
  const [clients, setClients] = useState([]);
  const [kpis, setKpis] = useState({ active_clients: 0, total_mrr: 0, domains_managed: 0, renewals_due_30_days: 0 });
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingGC, setIsSyncingGC] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionModal, setActionModal] = useState({ open: false, sub: null, action: '' });
  const [actionNote, setActionNote] = useState('');
  const [acting, setActing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [subsRes, ...legacyResults] = await Promise.all([
        api.get('/subscriptions/overview'),
        import('@/lib/api').then(m => m.clientsApi?.getAll({ sort_by: 'client_name', sort_order: 'asc' }).catch(() => ({ data: [] }))),
        import('@/lib/api').then(m => m.kpisApi?.get().catch(() => ({ data: {} }))),
        import('@/lib/api').then(m => m.packagesApi?.getAll().catch(() => ({ data: [] }))),
      ]);

      setSubs(subsRes.data);
      setClients(legacyResults[0]?.data || []);
      setKpis(legacyResults[1]?.data || { active_clients: 0, total_mrr: 0, domains_managed: 0, renewals_due_30_days: 0 });
      setPackages(legacyResults[2]?.data || []);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async () => {
    if (!actionModal.sub || !actionModal.action) return;
    setActing(true);
    try {
      await api.post('/subscriptions/manage', {
        site_id: actionModal.sub.site_id,
        action: actionModal.action,
        note: actionNote,
      });
      toast.success(`Subscription ${actionModal.action}d`);
      setActionModal({ open: false, sub: null, action: '' });
      setActionNote('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const handleGoCardlessSync = async () => {
    setIsSyncingGC(true);
    try {
      const { gocardlessApi } = await import('@/lib/api');
      const response = await gocardlessApi.sync();
      const result = response.data;
      toast.success(
        `GoCardless Sync: ${result.customers_found} customers, ${result.clients_created} created, ${result.clients_updated} updated`
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync with GoCardless');
    } finally {
      setIsSyncingGC(false);
    }
  };

  const getBillingColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'paused': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-500';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const totals = subs?.totals || {};
  const subscriptions = (subs?.subscriptions || []).filter(s =>
    s.performer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredClients = clients.filter(client => 
    client.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="billing-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Billing & Subscriptions</h2>
          <p className="text-slate-500">Manage monthly billing for all sites</p>
        </div>
        {tab === 'legacy' && (
          <Button onClick={handleGoCardlessSync} disabled={isSyncingGC} variant="outline" data-testid="gocardless-sync-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", isSyncingGC && "animate-spin")} />
            {isSyncingGC ? 'Syncing...' : 'Sync GoCardless'}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          className={cn("px-4 py-2 text-sm font-medium rounded-md transition-colors", tab === 'subscriptions' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
          onClick={() => setTab('subscriptions')}
          data-testid="tab-subscriptions"
        >
          Subscriptions
        </button>
        <button
          className={cn("px-4 py-2 text-sm font-medium rounded-md transition-colors", tab === 'legacy' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
          onClick={() => setTab('legacy')}
          data-testid="tab-legacy"
        >
          Legacy Clients
        </button>
      </div>

      {tab === 'subscriptions' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardDescription>Total Sites</CardDescription></CardHeader>
              <CardContent><p className="text-3xl font-bold text-slate-900">{totals.total_sites || 0}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Active Subscriptions</CardDescription></CardHeader>
              <CardContent><p className="text-3xl font-bold text-emerald-600">{totals.active || 0}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Monthly Revenue</CardDescription></CardHeader>
              <CardContent><p className="text-3xl font-bold text-emerald-600">{totals.monthly_revenue_display || '£0'}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Overdue</CardDescription></CardHeader>
              <CardContent><p className="text-3xl font-bold text-red-500">{totals.overdue || 0}</p></CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sites..." className="pl-10" data-testid="sub-search-input" />
            </div>
            <Badge variant="outline">{subscriptions.length} sites</Badge>
          </div>

          {/* Subscriptions Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Site</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Package</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Monthly</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Next Payment</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {subscriptions.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No matching sites</td></tr>
                    ) : (
                      subscriptions.map((sub) => (
                        <tr key={sub.site_id} className="hover:bg-slate-50" data-testid={`sub-row-${sub.slug}`}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900">{sub.performer_name}</p>
                              <p className="text-xs text-slate-400">{sub.slug}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{sub.package}</Badge></td>
                          <td className="px-4 py-3">
                            <Badge className={getBillingColor(sub.billing_status)}>{sub.billing_status}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{sub.monthly_display}/mo</td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {sub.next_payment_date ? new Date(sub.next_payment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`sub-actions-${sub.slug}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {sub.billing_status !== 'active' && (
                                  <DropdownMenuItem onClick={() => setActionModal({ open: true, sub, action: 'activate' })}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" /> Activate
                                  </DropdownMenuItem>
                                )}
                                {sub.billing_status === 'active' && (
                                  <DropdownMenuItem onClick={() => setActionModal({ open: true, sub, action: 'mark_paid' })}>
                                    <CreditCard className="w-4 h-4 mr-2 text-blue-500" /> Mark Paid
                                  </DropdownMenuItem>
                                )}
                                {sub.billing_status !== 'paused' && sub.billing_status !== 'cancelled' && (
                                  <DropdownMenuItem onClick={() => setActionModal({ open: true, sub, action: 'pause' })}>
                                    <Clock className="w-4 h-4 mr-2 text-amber-500" /> Pause
                                  </DropdownMenuItem>
                                )}
                                {sub.billing_status !== 'cancelled' && (
                                  <DropdownMenuItem onClick={() => setActionModal({ open: true, sub, action: 'cancel' })} className="text-red-600">
                                    <XCircle className="w-4 h-4 mr-2" /> Cancel
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'legacy' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardHeader className="pb-2"><CardDescription>Active Clients</CardDescription></CardHeader><CardContent><p className="text-3xl font-bold text-slate-900">{kpis.active_clients}</p></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardDescription>Monthly Revenue</CardDescription></CardHeader><CardContent><p className="text-3xl font-bold text-emerald-600">£{(kpis.total_mrr || 0).toFixed(2)}</p></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardDescription>Domains Managed</CardDescription></CardHeader><CardContent><p className="text-3xl font-bold text-slate-900">{kpis.domains_managed}</p></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardDescription>Renewals (30 days)</CardDescription></CardHeader><CardContent><p className="text-3xl font-bold text-amber-600">{kpis.renewals_due_30_days}</p></CardContent></Card>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search clients..." className="pl-10" />
            </div>
            <Badge variant="outline">{filteredClients.length} clients</Badge>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Client</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Package</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Payment</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">MRR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredClients.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">{clients.length === 0 ? 'No clients yet. Click "Sync GoCardless" to import.' : 'No matching clients'}</td></tr>
                    ) : (
                      filteredClients.map((client) => (
                        <tr key={client.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3"><div><p className="font-medium text-slate-900">{client.client_name}</p><p className="text-sm text-slate-500">{client.email}</p></div></td>
                          <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{packages.find(p => p.id === client.package_id)?.name || client.package_id || 'N/A'}</Badge></td>
                          <td className="px-4 py-3"><Badge className={getBillingColor(client.status)}>{client.status || 'Unknown'}</Badge></td>
                          <td className="px-4 py-3"><span className="text-sm text-slate-600 capitalize">{client.payment_method || 'N/A'}</span></td>
                          <td className="px-4 py-3 text-right"><span className="font-medium text-slate-900">£{(client.mrr || 0).toFixed(2)}</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Action Confirmation Modal */}
      <Dialog open={actionModal.open} onOpenChange={(open) => !open && setActionModal({ open: false, sub: null, action: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{actionModal.action} Subscription</DialogTitle>
            <DialogDescription>
              {actionModal.action === 'activate' && `Activate billing for ${actionModal.sub?.performer_name}`}
              {actionModal.action === 'mark_paid' && `Record payment for ${actionModal.sub?.performer_name} (${actionModal.sub?.monthly_display}/mo)`}
              {actionModal.action === 'pause' && `Pause billing for ${actionModal.sub?.performer_name}`}
              {actionModal.action === 'cancel' && `Cancel the subscription for ${actionModal.sub?.performer_name}. This cannot be undone easily.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Input value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="e.g. Paid via bank transfer" data-testid="action-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal({ open: false, sub: null, action: '' })}>Cancel</Button>
            <Button
              onClick={handleAction}
              disabled={acting}
              className={actionModal.action === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-action-btn"
            >
              {acting ? 'Processing...' : `${actionModal.action === 'mark_paid' ? 'Record Payment' : actionModal.action?.charAt(0).toUpperCase() + actionModal.action?.slice(1)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Super Admin Hub Component
export default function SuperAdminHub({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentView, setCurrentView] = useState('sites');
  const [builderMode, setBuilderMode] = useState(null); // null, 'create', 'import', or site object for edit

  // Determine active nav
  const getActiveNav = () => {
    if (location.pathname.includes('/orders')) return 'orders';
    if (location.pathname.includes('/enquiries')) return 'enquiries';
    if (location.pathname.includes('/newsletters')) return 'newsletters';
    if (location.pathname.includes('/backstage')) return 'backstage';
    if (location.pathname.includes('/users')) return 'users';
    if (location.pathname.includes('/billing')) return 'billing';
    if (location.pathname.includes('/content')) return 'content';
    return 'sites';
  };

  const handleCreateSite = () => {
    setBuilderMode('create');
  };

  const handleImportSite = () => {
    setBuilderMode('import');
  };

  const handleEditSite = (site) => {
    setBuilderMode(site);
  };

  const handleBuilderClose = () => {
    setBuilderMode(null);
  };

  const handleBuilderComplete = (siteId) => {
    setBuilderMode(null);
    // Could navigate to edit mode for the new site
  };

  // If in builder mode, show the builder
  if (builderMode) {
    if (builderMode === 'create') {
      return <SiteBuilder onClose={handleBuilderClose} onComplete={handleBuilderComplete} />;
    } else if (builderMode === 'import') {
      return <MigrationWizard onClose={handleBuilderClose} onComplete={handleBuilderComplete} />;
    } else {
      return <SiteEditor site={builderMode} onClose={handleBuilderClose} />;
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Boxit</h1>
                  <p className="text-xs text-slate-500 -mt-0.5">Admin Hub</p>
                </div>
              </div>
              
              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-1 ml-4">
                {navItems.map((item) => (
                  <Button
                    key={item.id}
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(item.path)}
                    className={cn(
                      "flex items-center gap-2",
                      getActiveNav() === item.id 
                        ? "bg-orange-50 text-orange-600 hover:bg-orange-100" 
                        : "text-slate-600 hover:text-slate-900"
                    )}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-4 h-4" strokeWidth={1.5} />
                    {item.label}
                  </Button>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600 hidden sm:block">{user?.name || user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onLogout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/" element={<SitesView onCreateSite={handleCreateSite} onEditSite={handleEditSite} onImportSite={handleImportSite} />} />
          <Route path="/enquiries" element={<EnquiriesView />} />
          <Route path="/orders" element={<OrdersView />} />
          <Route path="/newsletters" element={<NewslettersView />} />
          <Route path="/backstage" element={<BackstageCreditsView />} />
          <Route path="/users" element={<UsersView />} />
          <Route path="/billing" element={<BillingView />} />
          <Route path="/content" element={<SiteContentPage />} />
        </Routes>
      </main>
    </div>
  );
}
